import React, { useState } from 'react'
import { gql, useMutation } from '@apollo/client'
import { useSnackbar } from 'notistack'
import { useHistory } from 'react-router-dom'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import DeleteOutlineOutlined from 'components/material-symbol/icons/DeleteOutline'
import TextField from '@material-ui/core/TextField'
import Dialog from 'components/dialog'
import { makeStyles } from '@material-ui/core/styles'
import {
  getOfferingsPath,
  OFFERINGS_TAB,
} from 'pages/pga-coach/bookings/offerings/routes'

const useStyles = makeStyles(theme => ({
  titleRoot: {
    padding: theme.spacing(3, 7, 1, 3),
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 700,
    lineHeight: 1.3,
    color: '#172024',
  },
  subtitle: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.75),
  },
  content: {
    padding: theme.spacing(1, 3, 2),
  },
  bodyText: {
    margin: 0,
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
  formStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  actions: {
    padding: theme.spacing(2, 3, 3),
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
  },
}))

const CANCEL_GROUP_EVENT = gql`
  mutation CancelGroupEvent($groupEventId: ID!, $reason: String) {
    cancelGroupEvent(groupEventId: $groupEventId, reason: $reason) {
      success
      message
    }
  }
`

const CancelGroupEventDialog = ({ event, trigger, isOpen, onClose }) => {
  const classes = useStyles()
  const history = useHistory()

  const [cancelGroupEvent, { loading }] = useMutation(CANCEL_GROUP_EVENT, {
    onCompleted: data => {
      if (data.cancelGroupEvent.success) {
        enqueueSnackbar('Event cancelled successfully', {
          variant: 'success',
        })
        setTimeout(() => {
          history.push(getOfferingsPath(OFFERINGS_TAB.GROUP_EVENTS))
        }, 1000)
      } else {
        enqueueSnackbar(data.cancelGroupEvent.message, {
          variant: 'error',
        })
      }
    },
  })

  const onCancelEvent = () => {
    cancelGroupEvent({
      variables: {
        groupEventId: event.id,
        reason: cancellationReason,
      },
    })
  }

  const [cancellationReason, setCancellationReason] = useState('')
  const { enqueueSnackbar } = useSnackbar()
  const isControlled = isOpen != null

  const resetForm = () => {
    setCancellationReason('')
  }

  const handleClose = () => {
    resetForm()
    onClose?.()
  }

  const onChange = event => {
    setCancellationReason(event.target.value)
  }

  return (
    <Box mb={trigger ? 4 : 0}>
      <Dialog
        trigger={
          trigger ||
          (!isControlled ? (
            <Button type="button" color="primary">
              <Typography color="error"> Cancel This Event </Typography>
            </Button>
          ) : (
            undefined
          ))
        }
        isOpen={isOpen}
        onClose={handleClose}
        showCloseButton
        maxWidth="xs"
        fullWidth
      >
        {({ closeDialog }) => {
          const onDismiss = () => {
            resetForm()
            closeDialog()
          }

          return (
            <>
              <DialogTitle
                disableTypography
                classes={{ root: classes.titleRoot }}
              >
                <Typography component="h2" className={classes.title}>
                  Cancel Event
                </Typography>
                <Typography className={classes.subtitle}>
                  We'll notify any registrants that the event has been canceled.
                </Typography>
              </DialogTitle>
              <DialogContent classes={{ root: classes.content }}>
                <Typography className={classes.bodyText}>
                  You are responsible for refunding any payments collected as
                  part of registering for this event.
                </Typography>
                <Box className={classes.formStack}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    name="cancellationReason"
                    label="Reason for Cancellation (Optional)"
                    value={cancellationReason}
                    onChange={onChange}
                  />
                </Box>
              </DialogContent>
              <DialogActions className={classes.actions}>
                <Button color="primary" size="large" onClick={onDismiss}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  disableElevation
                  onClick={onCancelEvent}
                  disabled={loading}
                  startIcon={<DeleteOutlineOutlined fontSize="small" />}
                >
                  Cancel Event
                </Button>
              </DialogActions>
            </>
          )
        }}
      </Dialog>
    </Box>
  )
}

export default CancelGroupEventDialog
